import { css } from "@emotion/react"
import { motion } from "framer-motion"
import React, { useContext, useEffect } from "react"
import tw from "twin.macro"
import { Carousel, CarouselNavigation } from "../../../atoms/Carousel"
import { Image } from "../../../atoms/Image"
import { Hotspot } from "../../../organisms/XseriesModel/XseriesModel.d"
import { LanguageContext } from "../../../../contexts/Language"
import { generateInventoryLink } from "../../../../helpers"
import { ModelHighlightsHotspotViewerProps } from "./ModelHighlightsModuleProps"
import { PriceAndMPG } from "../../../molecules/PriceAndMPG"
import { ButtonLink } from "../../../atoms/Button"
import useTealiumEvent from "../../../../hooks/Tealium/useTealiumEvent"

const ModelHighlightsHotspotViewer: React.FC<
  ModelHighlightsHotspotViewerProps
> = ({
  images,
  currentSlideHotspots,
  activeSpot,
  setActiveSpot,
  setCurrentAngle,
  currentAngle,
  modelData,
}) => {
  const { trackTealEvent } = useTealiumEvent()

  // Setting active hotspot if it is not currently set or if it is not included in the current slide/angle's hotspots.
  useEffect(() => {
    const isActiveSpotOnCurrentSlide = currentSlideHotspots?.find(
      (hotspot: Hotspot) => hotspot?._key === activeSpot?._key,
    )
    if (!isActiveSpotOnCurrentSlide && currentSlideHotspots?.length > 0) {
      setActiveSpot(currentSlideHotspots[0])
    }
  }, [currentSlideHotspots])

  const { _, language } = useContext(LanguageContext)

  return (
    <div
      css={[tw`grid grid-cols-1 gap-5 w-full h-full lg:(grid-cols-3 mb-10 )`]}
    >
      <div
        css={[tw`flex flex-col gap-8 col-span-1 items-center lg:(items-start)`]}
      >
        <div
          css={[
            tw`text-center lg:text-left text-3xl font-semibold leading-10 tracking-widest uppercase`,
          ]}
        >
          <p css={[tw`text-3xl`]}>{modelData.year}</p>
          <p css={[tw`text-5xl`]}>{modelData.series.name}</p>
        </div>
        <div css={[tw`w-full max-w-sm`]}>
          <PriceAndMPG
            msrp={modelData.series.msrp}
            mpgCity={modelData.epa.mpgCity}
            mpgHighway={modelData.epa.mpgHighway}
            seriesName={modelData.series.name}
            seriesYear={modelData.year}
            asShownMsrp={modelData.costs.baseMSRP}
          />
        </div>
        <div css={[tw`flex flex-row w-full gap-5 justify-center`]}>
          <ButtonLink
            primary
            to={generateInventoryLink({
              series: modelData.series.name?.toLowerCase().replaceAll(" ", ""),
            })}
            css={[tw`w-full md:w-min whitespace-nowrap xl:w-full`]}
            analytics-id={`inventory:vehicle info:`}
            onClick={() => {
              trackTealEvent({
                tealium_event: "view_inventory_click",
                link_href: generateInventoryLink({
                  series: modelData.series.name
                    ?.toLowerCase()
                    .replaceAll(" ", ""),
                }),
                coupon_module_text: _("View Inventory"),
              })
            }}
          >
            {_("View Inventory")}
          </ButtonLink>
          <ButtonLink
            secondary
            to={`/offers/${modelData.series.name?.toLowerCase().replaceAll(" ", "")}`}
            css={[tw`w-full md:w-min whitespace-nowrap xl:w-full`]}
          >
            {_("View Offers")}
          </ButtonLink>
        </div>
      </div>
      <div css={[tw`col-span-1 lg:col-span-2`]}>
        <Carousel slides={images}>
          {({ activeSlide, changeSlide, slides }) => {
            const updateAngle = (slideId: number) => {
              if (slideId >= slides?.length) {
                setCurrentAngle(1)
              } else if (slideId < 0) {
                setCurrentAngle(4)
              } else {
                setCurrentAngle(slideId + 1)
              }
            }
            return (
              <div
                css={[tw`relative mb-12 mt-10 h-52 md:(h-96 mb-0 mx-10 my-10)`]}
              >
                {slides?.map((image, i) => {
                  const active = i + 1 === currentAngle
                  return (
                    <motion.div
                      viewport={{ once: true }}
                      initial={{
                        opacity: 0,
                        scale: 0.7,
                      }}
                      whileInView={{
                        opacity: 1,
                        scale: 1,
                      }}
                      transition={{ delay: 0.5, duration: 0.25 }}
                      css={[tw`absolute  h-full  mb-10  mx-5 lg:mx-16`]}
                    >
                      <div css={[tw`relative`]}>
                        <Image
                          imageData={image}
                          css={[
                            tw`opacity-0 relative`,
                            active && tw`opacity-100 `,
                          ]}
                        />
                        {currentSlideHotspots?.map((hotspot: Hotspot) => (
                          <motion.div
                            initial={{
                              opacity: 0,
                            }}
                            whileInView={{
                              opacity: 1,
                            }}
                            transition={{ delay: 0.5, duration: 0.5 }}
                            css={[
                              tw`absolute`,
                              css`
                                left: ${hotspot?.x - 3}%;
                                top: ${hotspot?.y - 4}%;
                                z-index: 10;
                              `,
                            ]}
                            onClick={() => {
                              setActiveSpot(hotspot)
                            }}
                          >
                            {active && (
                              <>
                                <div
                                  css={[
                                    tw``,
                                    activeSpot?._key === hotspot?._key &&
                                      tw`animate-pingAnimation absolute inline-flex w-full h-full rounded-full bg-gray-50 opacity-75 z-1`,
                                  ]}
                                ></div>
                                <div
                                  css={[
                                    tw`relative block rounded-full h-10 w-10 bg-gray-50 border-2 border-red-400 opacity-50 shadow-1 z-1`,
                                    activeSpot?._key === hotspot?._key &&
                                      tw`bg-red-500 opacity-50`,
                                  ]}
                                ></div>
                              </>
                            )}
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )
                })}
                <motion.div
                  viewport={{ once: true }}
                  initial={{
                    opacity: 0,
                  }}
                  whileInView={{
                    opacity: 1,
                  }}
                  transition={{ delay: 1, duration: 0.25 }}
                >
                  <CarouselNavigation
                    direction="prev"
                    color="white"
                    onClick={() => {
                      changeSlide(activeSlide - 1)
                      updateAngle(activeSlide - 1)
                    }}
                    css={[
                      tw`absolute left-[15%] -bottom-6 bg-black`,
                      tw` md:(hover:bg-gray-600 left-[38%])`,
                      css`
                        &:focus-visible {
                          outline-color: #000;
                        }
                      `,
                    ]}
                  />
                  <CarouselNavigation
                    direction="next"
                    color="white"
                    onClick={() => {
                      changeSlide(activeSlide + 1)
                      updateAngle(activeSlide + 1)
                    }}
                    css={[
                      tw`absolute right-[15%] -bottom-6 bg-black`,
                      tw` md:(hover:bg-gray-600 right-[38%])`,
                      tw`focus-visible:outline-[#000]`,
                    ]}
                  />
                </motion.div>
              </div>
            )
          }}
        </Carousel>
      </div>
    </div>
  )
}

export default ModelHighlightsHotspotViewer
